import { BACKEND_URL } from "../lib/backend";
/**
 * ReportCard — authoritative report/research card (redesign item 3, tier 1).
 * Dark editorial surface #0f2529, #8fd4dc accent, serif 21px title.
 * SEO expansion (3/9): clean title, sentence-complete excerpt, primary link
 * to the INTERNAL brief page (/reports/{slug}), secondary "Official source ↗",
 * category chip → hub page, access chip.
 */
import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ExternalLink, FileText, Calendar, Lock, ArrowRight } from "lucide-react";

const API = `${BACKEND_URL}/api`;

const trackClick = (url) => {
  if (!url) return;
  axios.post(`${API}/news/click`, { url }).catch(() => { /* ignore */ });
};

const fmtDate = (iso, precision) => {
  if (!iso) return "Undated";
  try {
    const d = new Date(iso);
    if (precision === "year") return String(d.getUTCFullYear());
    if (precision === "month") return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const ReportCard = ({ report, darkMode, compact = false }) => {
  const dm = darkMode;
  const access = report.access && report.access !== "Not specified" ? report.access : null;
  const isFree = /free/i.test(access || "");
  const isPdf = /\.pdf(\?|$)/i.test(report.url || "");
  const briefUrl = report.brief_url || `/reports/${report.slug || report.id}`;
  const title = report.clean_title || report.title;
  const excerpt = report.excerpt || report.summary || report.focus;

  return (
    <article
      data-testid="report-card"
      className={`relative rounded-2xl overflow-hidden transition-all duration-200 group hover:-translate-y-0.5 ${compact ? "p-4" : "p-5"}`}
      style={{
        background: "#0f2529",
        border: dm ? "1px solid rgba(143,212,220,0.13)" : "1px solid rgba(15,37,41,0.9)",
        boxShadow: dm ? "none" : "0 2px 10px rgba(16,40,44,0.1)",
      }}
    >
      <div className="absolute top-3 right-3 z-10">
        <span
          data-testid="report-badge"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{ background: "rgba(143,212,220,0.12)", color: "#8fd4dc", border: "1px solid rgba(143,212,220,0.3)" }}
        >
          <FileText className="w-3 h-3" />
          {report.report_type || "Report"}
        </span>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#8fd4dc" }}>
        {report.organisation || "Research"}
      </div>

      <Link
        to={briefUrl}
        data-testid="report-title-link"
        className={`block font-serif font-bold leading-snug pr-16 text-white transition-colors group-hover:text-[#8fd4dc] ${compact ? "text-lg" : "text-[21px]"}`}
      >
        {title}
      </Link>

      {excerpt && (
        <p className="mt-2 text-sm leading-relaxed" style={{ color: "#a9c2c6" }} data-testid="report-excerpt">
          {excerpt}
        </p>
      )}

      <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1.5 text-[11px]" style={{ color: "rgba(169,194,198,0.75)" }}>
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {fmtDate(report.published_date, report.published_date_precision)}
        </span>
        {report.category_slug && (
          <Link
            to={`/reports/category/${report.category_slug}`}
            data-testid="report-category-chip"
            className="px-2 py-0.5 rounded-full transition-colors hover:bg-[rgba(143,212,220,0.22)]"
            style={{ background: "rgba(143,212,220,0.1)", color: "#8fd4dc" }}
          >
            {report.category_label || report.category}
          </Link>
        )}
        {access && (
          <span className="flex items-center gap-1" data-testid="report-access-chip">
            {isFree ? null : <Lock className="w-3 h-3" />}
            {access}
          </span>
        )}
        {isPdf && (
          <span data-testid="report-pdf-tag" className="font-bold uppercase tracking-wider" style={{ color: "#8fd4dc" }}>
            PDF
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs">
        <Link
          to={briefUrl}
          data-testid="report-brief-link"
          className="inline-flex items-center gap-1 font-semibold transition-colors hover:underline underline-offset-2"
          style={{ color: "#8fd4dc" }}
        >
          Read the brief <ArrowRight className="w-3 h-3" />
        </Link>
        <a
          href={report.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick(report.url)}
          className="inline-flex items-center gap-1 transition-colors hover:underline underline-offset-2"
          style={{ color: "rgba(169,194,198,0.8)" }}
          data-testid="report-open-link"
        >
          Official source <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </article>
  );
};

export default ReportCard;
